import { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { verifyEmail } from '../api/auth'
import { apiRequest } from '../api/client'
import { TokenActionCard, type TokenActionState } from '../components/TokenActionCard'
import { useT } from '../i18n/context'
import { Button } from '../components/ui/Button'
import { useAuthStore } from '../stores/auth'
import type { UserProfile } from '../types/api'

export function VerifyEmailPage() {
    const t = useT()
    const [params] = useSearchParams()
    const token = params.get('token') ?? ''
    const [state, setState] = useState<TokenActionState>('loading')
    // Token ist Single-Use: Request trotz StrictMode-Double-Mount nur einmal
    const started = useRef(false)

    useEffect(() => {
        if (started.current || !token) return
        started.current = true
        verifyEmail(token)
            .then(async () => {
                setState('success')
                // Bestehende Session: Profil neu laden (isEmailVerified)
                if (useAuthStore.getState().status === 'authenticated') {
                    try {
                        const profile = await apiRequest<UserProfile>(
                            '/users/profile',
                        )
                        useAuthStore.getState().setUser(profile)
                    } catch {
                        /* Profil-Reload ist optional */
                    }
                }
            })
            .catch(() => setState('error'))
    }, [token])

    const authenticated =
        useAuthStore(s => s.status) === 'authenticated'

    if (!token) {
        return (
            <TokenActionCard state='error' title={t('verify.invalidTitle')}>
                <p>{t('verify.noToken')}</p>
                <LinkButton to='/login' label={t('verify.toLogin')} />
            </TokenActionCard>
        )
    }

    return (
        <TokenActionCard
            state={state}
            title={
                state === 'success'
                    ? t('verify.successTitle')
                    : state === 'error'
                      ? t('verify.errorTitle')
                      : t('verify.verifying')
            }
        >
            {state === 'error' && (
                <p>{t('verify.errorDesc')}</p>
            )}
            {state === 'success' && authenticated && (
                <LinkButton to='/settings' label={t('verify.toSettings')} />
            )}
            {!authenticated && (
                <LinkButton to='/login' label={t('verify.toLogin')} />
            )}
        </TokenActionCard>
    )
}

function LinkButton({ to, label }: { to: string; label: string }) {
    return (
        <Link to={to}>
            <Button variant='secondary'>{label}</Button>
        </Link>
    )
}
